/**
 * reviewSendGuard — pure guard for the "Review and Send" primary CTA.
 *
 * PT-53A W2.1 · DEF-102 · FL-02.
 *
 * ─── Why this exists ─────────────────────────────────────────────────────
 *
 * `QuoteBuilderPage.handleReviewSend` was a silent no-op on falsy `targetId`:
 *
 *   let savedId = null;
 *   if (isDirty || (!quoteId && !id)) {
 *     savedId = await onLeave();
 *   }
 *   const targetId = quoteId || id || savedId;
 *   if (targetId) {
 *     navigate(`/quotes/${targetId}/review`);
 *   }
 *   // <-- falls off the end. No toast, no error, no navigation.
 *
 * If `onLeave()` returned null (save failed / cancelled / auth expired /
 * validation rejected), the user clicked "Review and Send" and NOTHING
 * happened. `§silent success is worse than visible failure` — this is
 * the worst possible failure mode: the primary CTA reports nothing while
 * doing nothing.
 *
 * ─── This module ─────────────────────────────────────────────────────────
 *
 * Extract the decision into a pure function that returns a discriminated
 * union. The async wrapper in QuoteBuilderPage becomes a thin dispatcher:
 * on 'navigate' it calls `navigate()`, on 'error' it shows a toast, on
 * 'skip' it's a preconditions gate (button was somehow clicked while
 * disabled — belt-and-suspenders).
 *
 * All branches now do SOMETHING observable — no fall-off-the-end.
 */

/**
 * @typedef {(
 *   | { action: 'navigate', to: string }
 *   | { action: 'error', code: string, message: string }
 *   | { action: 'skip', code: string }
 * )} ReviewSendDecision
 */

/**
 * @param {object} params
 * @param {string | null | undefined} params.quoteId   builder state id (post-save)
 * @param {string | null | undefined} params.urlId     `:id` from useParams
 * @param {string | null | undefined} params.savedId   whatever onLeave() returned
 * @param {boolean} params.hasCustomer                 prefill.customerId truthy
 * @param {boolean} params.hasLines                    lineItems.length > 0
 * @returns {ReviewSendDecision}
 */
export function computeReviewSendTarget({ quoteId, urlId, savedId, hasCustomer, hasLines }) {
  // Preconditions — the button should be disabled in these states but if
  // it wasn't, don't proceed. Return 'skip' so the caller can log or noop.
  if (!hasLines) return { action: 'skip', code: 'no-lines' };
  if (!hasCustomer) return { action: 'skip', code: 'no-customer' };

  // Resolution: prefer the state-tracked id (already saved this session),
  // fall back to the URL param, fall back to whatever onLeave returned.
  const targetId = quoteId || urlId || savedId;

  if (targetId) {
    return { action: 'navigate', to: `/quotes/${targetId}/review` };
  }

  // The DEF-102 branch. `onLeave()` didn't yield an id, `quoteId` and
  // `urlId` are both null. Something in the save path failed silently.
  // MUST surface this instead of falling off the end.
  return {
    action: 'error',
    code: 'save-required',
    message: "Couldn't save this quote — please try again before continuing to Review & Send.",
  };
}
